import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, map, of, share } from 'rxjs';
import { catchError } from 'rxjs/operators';
import _ from 'lodash';

import { GlobalMessages } from '@Services/global-messages';
import { LoggingService } from '@Services/logging.service';
import { LoadingService } from '@Services/loading.service';
import { PolicyService } from '@Core/services/codegen/policy.service';

import { SCPAccount } from '@Core/models/scp-account';
import { InterfaceStatus, PolicyIntegrity, SCPPolicy } from '@Core/models/scp-policy';
import { PolicyTransaction, PolicyTransactionDetail, TransactionTypesModel } from '@Core/models/policy-transaction';
import { ManagedDocumentInfo } from '@Core/models/documents/documents';
import { DisplayDocumentURL, HistoricalForm, HistoricalFormsJobId } from '@Core/models/documents/worksheet-form-doc';
import { JsonApiErrorResponse, JsonApiError, JSON_API_ERROR_RESPONSE } from '@Core/models/errors';
import { ApiError } from './scp-user.service';
import { RecoveryStatus } from '@Core/models/recoverable-policy';
import { SearchState } from '@Core/states/search/search.reducer';
import { HttpParameterCodec } from '@angular/common/http';
import { Product } from '@Core/models/product';
import { AccountNumberFields } from '@Core/models/account-number';


class Response extends HttpResponse<any> { }

@Injectable({ providedIn: 'root' })
export class PolicyServiceExt extends PolicyService {

    constructor(protected http: HttpClient,
        protected globalMessages: GlobalMessages,
        protected loggingService: LoggingService,
        protected loadingService: LoadingService) {
        super(http, globalMessages, loggingService, loadingService);
    }

    public GetAccountsWithFilters(filters: Partial<SearchState>, simpleSearch: string = null): Observable<SCPAccount[]> {
        const url = PolicyService.baseUrl + "Accounts";

        // Map SearchState keys to API filter keys
        const paramMap: Record<string, string> = {
            sharedAccount: 'filter[AccountName]',
            sharedDba: 'filter[DBA]',
            sharedProducerCode: 'filter[ProducerCode]',
            sharedProducerName: 'filter[ProducerName]',
            advancedSearchSimpleSearch: 'filter[SimpleSearchCriteria]',
            sharedCreatedBy: 'filter[CreatedBy]',
            sharedLastModifiedBy: 'filter[LastModifiedBy]',
            sharedLastModifiedDateStart: 'filter[LastModifiedDateFrom]',
            sharedLastModifiedDateEnd: 'filter[LastModifiedDateTo]',
            acctNumber: 'filter[AccountNumber]',
            acctDescriptionOfOperations: 'filter[DescriptionOfOperations]',
            acctFEIN: 'filter[FEIN]',
            // Add more mappings as needed
        };

        let params: HttpParams = new HttpParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && paramMap[key]) {
                // Convert Date objects to ISO strings
                if (value instanceof Date) {
                    params = params.append(paramMap[key], value.toISOString());
                } else {
                    params = params.append(paramMap[key], String(value));
                }
            }
        });

        if (simpleSearch)
            params = params.append("simpleSearchCriteria", simpleSearch);

        return this.http.get<any>(url, { params }).pipe(
            map((response) => response.Content.data!.map((x) => this.mapAccountSearchResult(x)))
        );
    }

    mapAccountSearchResult({ attributes, id }): SCPAccount {
        return {
            isDirty: false,
            type: 'account',
            isFavorite: false,
            id: id ?? '',
            name: attributes?.name ?? '',
            address_1: attributes?.address_1 ?? '',
            address_2: attributes?.address_2 ?? '',
            dba: attributes?.dba ?? '',
            city: attributes?.city ?? '',
            state: attributes?.state ?? '',
            zipCode: attributes?.zipCode ?? '',
            descriptionOfOperations: attributes?.descriptionOfOperations ?? '',
            fein: attributes?.fein ?? '',
            producerCode: attributes?.producerCode ?? '',
            producerName: attributes?.producerName ?? '',
            extendedAccountInfo: attributes?.extendedAccountInfo ?? '',
        };
    }

    public GetPoliciesExt(simpleSearch: string = null, resultSize: number = 100, accountCode: string = null): Observable<SCPPolicy[]> {
        // Build the url
        var url = PolicyService.baseUrl + "Policies";

        let params: HttpParams = new HttpParams()
        params = params.append("resultSize", resultSize);
        if (simpleSearch)
            params = params.append("simpleSearchCriteria", simpleSearch);
        if (accountCode)
            params = params.append("accountId", accountCode);

        var request = this.http.get<any>(url, {
            params: params
        }).pipe(
            map((response) => response.Content.data!.map((x) => this.mapPolicySearchResult(x))));

        return request;
    }

    mapPolicySearchResult({ attributes, id }): SCPPolicy {
        return {
            //from dto
            type: 'policy',
            id: id!,
            //from detail attributes
            accountId: attributes?.accountId ?? '',
            accountUID: attributes?.accountUID ?? 0,
            activeTransactionId: attributes?.activeTransactionID ?? 0,
            address_1: attributes?.address_1 ?? '',
            address_2: attributes?.address_2 ?? '',
            cancellationDate: this.toDate(attributes?.cancellationDate),
            city: attributes?.city ?? '',
            commitDate: this.toDate(attributes?.commitDate),
            committedBy: attributes?.committedBy ?? '',
            //controllingSystem: this.mapControllingSystemEnum(
            //    attributes?.controllingSystem,
            //),
            createdBy: attributes?.createdBy ?? '',
            creationDate: attributes?.creationDate
                ? new Date(attributes?.creationDate)
                : new Date(),
            dba: attributes?.dba ?? '',
            email: attributes?.email ?? '',
            endorsementEffectiveDate: attributes?.endorsementEffectiveDate
                ? new Date(attributes?.endorsementEffectiveDate)
                : new Date(),
            expirationDate: attributes?.expirationDate
                ? new Date(attributes?.expirationDate)
                : new Date(),
            fax: attributes?.fax ?? '',
            inceptionDate: attributes?.inceptionDate
                ? new Date(attributes?.inceptionDate)
                : new Date(),
            insuredName: attributes?.insuredName ?? '',
            policyIntegrity:
                (attributes?.integrity as PolicyIntegrity | undefined) ?? null,
            interfaceStatus: attributes?.interfaceStatus?.valueOf() ?? null,
            isPolicy: attributes?.isPolicy ?? false,
            issueDate: attributes?.issueDate
                ? new Date(attributes?.issueDate)
                : new Date(),
            lastModifiedBy: attributes?.lastModifiedBy ?? '',
            lastModifiedDate: attributes?.lastModifiedDate
                ? new Date(attributes?.lastModifiedDate)
                : new Date(),
            lob: attributes?.lob ?? '',
            nextInterimAuditTransactionDate:
                attributes?.nextInterimAuditTransactionDate
                    ? new Date(attributes?.nextInterimAuditTransactionDate)
                    : new Date(),
            phone: attributes?.phone ?? '',
            policyDescription: attributes?.policyDescription ?? '',
            policyNumber: attributes?.policyNumber ?? '',
            policyPrimaryStatus: attributes?.policyPrimaryStatus ?? '',
            policyVersion: attributes?.policyVersion ?? '',
            premium: attributes?.premium ?? 0,
            producerId: attributes?.producerId ?? '',
            producerName: attributes?.producerName ?? '',
            productId: attributes?.productId ?? 0,
            productName: attributes?.productName ?? '',
            quoteNumber: attributes?.quoteNumber ?? '',
            quoteVersion: attributes?.quoteVersion ?? '',
            ratingCompanyId: attributes?.ratingCompanyId ?? '',
            ratingState: attributes?.ratingState ?? '',
            state: attributes?.state ?? '',
            storageId: attributes?.storageId ?? '',
            subjectToAudit: attributes?.subjectToAudit
                ? [...attributes?.subjectToAudit]
                : [],
            transactionDescription: attributes?.transactionDescription ?? '',
            writtenPremium: attributes?.writtenPremium ?? 0,
            zipCode: attributes?.zipCode ?? '',

            //UI Only
            version: '1',
            uw: '',
            isFavorite: false,
            isRecent: false,
            //extendedAccountInfo: attributes?.extendedAccountInfo ?? '',
        };
    }

    getPolicy(id: string): Observable<SCPPolicy> {
        var url = PolicyService.baseUrl + 'Policies/' + id;

        var request = this.http.get<any>(url).pipe(
            map((response) => this.mapPolicy(response.Content.data!))
        );

        return request;
    }

    mapPolicy({ attributes, type, id }): SCPPolicy {
        const uiPolicy: SCPPolicy = {
            //from dto
            type: 'policy',
            id: id!,
            //from detail attributes
            accountId: attributes?.accountId ?? '',
            accountUID: attributes?.accountUID ?? 0,
            activeTransactionId: attributes?.activeTransactionID ?? 0,
            address_1: attributes?.address_1 ?? '',
            address_2: attributes?.address_2 ?? '',
            cancellationDate: this.toDate(attributes?.cancellationDate),
            city: attributes?.city ?? '',
            commitDate: this.toDate(attributes?.commitDate),
            committedBy: attributes?.committedBy ?? '',
            createdBy: attributes?.createdBy ?? '',
            creationDate: attributes?.creationDate
                ? new Date(attributes?.creationDate)
                : new Date(),
            dba: attributes?.dba ?? '',
            email: attributes?.email ?? '',
            endorsementEffectiveDate: attributes?.endorsementEffectiveDate
                ? new Date(attributes?.endorsementEffectiveDate)
                : new Date(),
            expirationDate: attributes?.expirationDate
                ? new Date(attributes?.expirationDate)
                : new Date(),
            fax: attributes?.fax ?? '',
            inceptionDate: attributes?.inceptionDate
                ? new Date(attributes?.inceptionDate)
                : new Date(),
            insuredName: attributes?.insuredName ?? '',
            policyIntegrity:
                (attributes?.integrity as PolicyIntegrity | undefined) ?? null,
            interfaceStatus: attributes?.interfaceStatus?.valueOf() ?? null,
            isPolicy: attributes?.isPolicy ?? false,
            issueDate: attributes?.issueDate
                ? new Date(attributes?.issueDate)
                : new Date(),
            lastModifiedBy: attributes?.lastModifiedBy ?? '',
            lastModifiedDate: attributes?.lastModifiedDate
                ? new Date(attributes?.lastModifiedDate)
                : new Date(),
            lob: attributes?.lob ?? '',
            nextInterimAuditTransactionDate:
                attributes?.nextInterimAuditTransactionDate
                    ? new Date(attributes?.nextInterimAuditTransactionDate)
                    : new Date(),
            phone: attributes?.phone ?? '',
            policyDescription: attributes?.policyDescription ?? '',
            policyNumber: attributes?.policyNumber ?? '',
            policyPrimaryStatus: attributes?.policyPrimaryStatus ?? '',
            policyVersion: attributes?.policyVersion ?? '',
            premium: attributes?.premium ?? 0,
            producerId: attributes?.producerId ?? '',
            producerName: attributes?.producerName ?? '',
            productId: attributes?.productId ?? 0,
            productName: attributes?.productName ?? '',
            quoteNumber: attributes?.quoteNumber ?? '',
            quoteVersion: attributes?.quoteVersion ?? '',
            ratingCompanyId: attributes?.ratingCompanyId ?? '',
            ratingState: attributes?.ratingState ?? '',
            state: attributes?.state ?? '',
            storageId: attributes?.storageId ?? '',
            subjectToAudit: attributes?.subjectToAudit
                ? [...attributes?.subjectToAudit]
                : [],
            transactionDescription: attributes?.transactionDescription ?? '',
            writtenPremium: attributes?.writtenPremium ?? 0,
            zipCode: attributes?.zipCode ?? '',

            //UI Only
            version: '1',
            uw: '',
            isFavorite: false,
            isRecent: false,
        };
        return uiPolicy;
    }

    public GetPoliciesWithFilters(filters: Partial<SearchState>, simpleSearch: string = null, resultSize: number = 100): Observable<SCPPolicy[]> {
        const url = PolicyService.baseUrl + "Policies";

        // Map SearchState keys to API filter keys
        const paramMap: Record<string, string> = {
            sharedAccount: 'accountId',
            pqNumber: 'policyNumber',
            sharedInsuredName: 'insuredName',
            sharedDba: 'dba',
            pqEffectiveDateFrom: 'effectiveDateFrom',
            pqEffectiveDateTo: 'effectiveDateTo',
            pqExpirationDateFrom: 'expirationDateFrom',
            pqExpirationDateTo: 'expirationDateTo',
            pqPolicyStatus: 'policyStatus',
            sharedProducerCode: 'producerCode',
            sharedProducerName: 'producerName',
            pqRatingCompany: 'ratingCompany',
            pqStateOfDomicile: 'stateOfDomicile',
            advancedSearchSimpleSearch: 'simpleSearchCriteria',
            sharedCreatedBy: 'createdBy',
            sharedLastModifiedBy: 'lastModifiedBy',
            sharedLastModifiedDateStart: 'lastModifiedDateFrom',
            sharedLastModifiedDateEnd: 'lastModifiedDateTo',
            // Add more mappings as needed
        };

        let params: HttpParams = new HttpParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && paramMap[key]) {
                // Convert Date objects to ISO strings
                if (value instanceof Date) {
                    params = params.append(paramMap[key], value.toISOString());
                } else {
                    params = params.append(paramMap[key], String(value));
                }
            }
        });
        params = params.append('product', filters.pqProduct?.id || '');
        params = params.append("resultSize", resultSize);
        if (simpleSearch)
            params = params.append("simpleSearchCriteria", simpleSearch);

        return this.http.get<any>(url, { params }).pipe(
            map((response) => response.Content.data!.map((x) => this.mapPolicySearchResult(x)))
        );
    }

    getTransactionTypes(id: string): Observable<TransactionTypesModel> {
        var url = PolicyService.baseUrl + 'Policies/' + id + '/transaction-types';

        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.set('Accept', 'application/json');

        var request = this.http.get<any>(url, {
            headers: headers
        }).pipe(map(data => this.mapTransactionTypes(data.Content.data)));

        return request;
    }

    getTransactionTypesForTransaction(
        storageId: string,
        transactionId: number,
    ): Observable<TransactionTypesModel> {
         var url = PolicyService.baseUrl + 'Policies/' + storageId + '/transaction-types/' + transactionId;

        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.set('Accept', 'application/json');

        var request = this.http.get<any>(url, {
            headers: headers
        }).pipe(map(data => this.mapTransactionTypes(data.Content.data)));

        return request;
    }

    mapTransactionTypes({
        attributes: { allowEditPolicies, allowViewPolicies, availableTransactions },
    }: any): TransactionTypesModel {
        return {
            allowEditPolicies: allowEditPolicies ?? false,
            allowViewPolicies: allowViewPolicies ?? false,
            availableTransactions: availableTransactions ?? [],
        };
    }

    getPolicyTransaction(storageId: string, transactionId: number): Observable<PolicyTransactionDetail | undefined> {
        var url = PolicyService.baseUrl + 'Transactions/' + storageId + '-' + transactionId;

        var request = this.http.get<any>(url).pipe(
            map((data) => this.mapTransactionDetail(data.Content.data))
        );
        return request;
    }

    mapTransaction({
        id,
        attributes
    }: any): PolicyTransaction {
        const trans: PolicyTransaction = {
            id: id ?? '',
            storageID: attributes?.storageID ?? '',
            transactionID: attributes?.transactionID ?? 0,
            transactionDate: attributes?.transactionDate ?? '',
            primaryStatus: attributes?.primaryStatus ?? '',
            inceptionDate: attributes?.inceptionDate ?? '',
            expirationDate: attributes?.expirationDate ?? '',
            lob: attributes?.lob ?? '',
            isPolicy: attributes?.isPolicy ?? false,
            writtenPremium: attributes?.writtenPremium ?? 0,
            transactionPremium: attributes?.transactionPremium ?? 0,
            issueDate: attributes?.issueDate ?? '',
            endorsementEffectiveDate: attributes?.endorsementEffectiveDate ?? '',
            cancellationDate: attributes?.cancellationDate ?? '',
            policyIntegrity: attributes?.policyIntegrity ?? 0,
            commitDate: attributes?.commitDate ?? '',
            premium: attributes?.premium ?? 0,
            committedBy: attributes?.committedBy ?? '',
            interfaceStatus:
                (attributes?.interfaceStatus as any as InterfaceStatus) ?? undefined,
            description: attributes?.description ?? '',
            formsInRepository: attributes?.formsInRepository ?? false,
        };
        return trans;
    }

    mapTransactionDetail(
        transaction: any,
    ): PolicyTransactionDetail {
        return {
            ...this.mapTransaction(transaction),
            validNextTransactions:
                transaction.attributes?.validNextTransactions ?? '',
        };
    }

    GetPolicyTransactions(storageID: string): Observable<PolicyTransaction[]> {
       // Build the url
        var url = PolicyService.baseUrl + "/Transactions";
        let params: HttpParams = new HttpParams().set('storageId', storageID);
        // Make the request
        var request = this.http.get<any>(url, { params }).pipe(
            map((resp) => {
            const data = resp.Content?.data;
            if (!data) return [];
            // If data is an array, map each item; if it's a single object, wrap in array
            const items = Array.isArray(data) ? data : [data];
            return items.map((x) => this.mapTransaction(x));
            }),
            catchError((err: HttpErrorResponse) => {
              if (404 === err.status)
                 return of([]);
              throw this.handleError(err);
            })
        );

        return request;
  }

    GetDocumentInfo(
        jobId: string,
        groupId: string,
    ): Observable<ManagedDocumentInfo | undefined> {
        // Build the url
        var url = PolicyService.baseUrl + "Documents/DocumentInfo";
        let params: HttpParams = new HttpParams()
        params = params.append("groupId", groupId);
        params = params.append("jobId", jobId);

        // Make the request
        var request = this.http.get<any>(url, {
            params: params
        }).pipe(map((resp) => this.mapDocumentInfo(resp)),
            catchError((err: HttpErrorResponse) => {
                if (404 === err.status) return of(undefined);
                throw this.handleError(err);
            }));

        return request;
    }

    mapDocumentInfo(docInfo: any): ManagedDocumentInfo {
        return {
            creationTime: docInfo.data?.attributes?.creationTime,
            documentType: docInfo.data?.attributes?.documentType as number,
            shortDescription: docInfo.data?.attributes?.shortDescription,
        };
    }

    getDocumentUrl(
        jobId: string,
        groupId: string,
    ): Observable<DisplayDocumentURL> {
        // Build the url
        var url = PolicyService.baseUrl + "Documents";
        let params: HttpParams = new HttpParams()
        params = params.append("groupId", groupId);
        params = params.append("jobId", jobId);

        // Make the request
        var request = this.http.get<any>(url, {
            params: params
        }).pipe(
            map(({ Content }) => this.mapDocumentUrl(Content.data, jobId)),
            catchError(this.handleError),
        );

        return request;
    }

    mapDocumentUrl(doc: any, jobId: string): DisplayDocumentURL {
        return {
            blobUrl: doc.attributes?.blobUrl ?? '',
            id: doc.id ?? '',
            type: doc.type ?? '',
            jobId: jobId,
        };
    }

    getLockInfo(storageId: string) {
        var url = PolicyService.baseUrl + 'PolicyLocks/' + storageId;
        var request = this.http.get<any>(url).pipe(
            map((data) => this.mapPolicyLockInfo(data.Content.data))
        );
        return request;
    }

    mapPolicyLockInfo({ attributes }: any): any {
        return {
            isLocked: attributes?.isLocked ?? false,
            lockedBy: attributes?.lockedBy,
            lockedWhen: this.toDate(attributes?.lockedWhen),
        };
    }

    getRecoveryInfoForPolicy(storageId: string, transactionId: number) {
        var url = PolicyService.baseUrl + 'PolicyLocks/RecoveryInfo/' + storageId + '-' + transactionId;

        var request = this.http.get<any>(url).pipe(
            map((data) => this.mapPolicyRecoveryInfo(data.Content.data))
        );
        return request;
    }

    getRecoveryInfoForRecoveryInstance(recoveryInstanceId: string) {
        var encodedId = encodeURIComponent(recoveryInstanceId);
        var url = PolicyService.baseUrl + 'PolicyLocks/RecoveryStatus/' + encodedId;

        var request = this.http.get<any>(url).pipe(
            map((data) => this.mapPolicyRecoveryInfo(data.Content.data))
        );
        return request;
    }

    mapPolicyRecoveryInfo({ attributes }: any): any {
        return {
            instanceId: attributes?.instanceID ?? undefined,
            status: (attributes?.status ?? RecoveryStatus.NotOpen),
        };
    }

    public getRatingCompanies(): Observable<string[]> {
        const url = PolicyService.baseUrl + "ReferenceData/RatingCompanies";
        var request = this.http.get<any>(url).pipe(
            map(data => data.Content?.data?.attributes?.ratingCompanies)
        );
        return request;
    }

    public getStates(): Observable<string[]> {
        const url = PolicyService.baseUrl + "CityStateZip/states";
        var request = this.http.get<any>(url).pipe(
            map(data => data.Content?.data?.attributes?.states)
        );
        return request;
    }

    public getPolicyStatuses(): Observable<string[]> {
        const url = PolicyService.baseUrl + "ReferenceData/PolicyStatuses";
        var request = this.http.get<any>(url).pipe(
            map(data => data.Content?.data?.attributes?.policyStatuses)
        );
        return request;
    }

    public lookupLegalEntities(): Observable<string[]> {
        const url = PolicyService.baseUrl + "Lookup/ReferenceData/LegalEntities"
        var request = this.http.get<any>(url).pipe(
            map(data => data.Content?.data?.attributes?.legalEntities)
        );
        return request;
    }

    tempSavePolicy(recoveryInstanceId: string) {
        var encodedId = encodeURIComponent(recoveryInstanceId);

        var url = PolicyService.baseUrl + 'PolicyLocks/' + encodedId + '/actions/tempsave';

        var request = this.http.post<any>(url, null).pipe(
            map((data) => data.Content.data)
        );
        return request;
    }

    unlock(recoverableStorageId: string, recoveryInstanceId: string, forceUnlock: boolean = false) {
        var url = PolicyService.baseUrl + 'PolicyLocks/' + recoverableStorageId + '/actions/unlock';

        var encoder = new CustomHttpParameterCodec();
        let params: HttpParams = new HttpParams({ encoder: encoder })
        params = params.set('policyId', recoveryInstanceId);
        params = params.set('force', forceUnlock.toString());

        return this.http.post<any>(url, null, { params });
    }

    public lookupCities(state: string = "", zip: string = "", county: string = "") {
        const url = PolicyService.baseUrl + "Lookup/CityStateZip/cities"
        let params: HttpParams = new HttpParams()
        params = params.append("state", state);
        params = params.append("zipCode", zip);
        params = params.append("county", county);

        var request = this.http.get<any>(url, { params }).pipe(
            map(data => data.Content?.data?.attributes?.cities)
        );
        return request;
    }

    public lookupStates(city: string = "", zip: string = "", county: string = "") {
        const url = PolicyService.baseUrl + "Lookup/CityStateZip/states"
        let params: HttpParams = new HttpParams()
        params = params.append("city", city);
        params = params.append("zipCode", zip);
        params = params.append("county", county);

        var request = this.http.get<any>(url, { params }).pipe(
            map(data => data.Content?.data?.attributes?.states)
        );
        return request;
    }

    public lookupZipCodes(city: string = "", state: string = "", county: string = "") {
        const url = PolicyService.baseUrl + "Lookup/CityStateZip/zipcodes"
        let params: HttpParams = new HttpParams()
        params = params.append("state", state);
        params = params.append("city", city);
        params = params.append("county", county);

        var request = this.http.get<any>(url, { params }).pipe(
            map(data => data.Content?.data?.attributes?.zipCodes)
        );
        return request;
    }

    public lookupCounties(city: string = "", state: string = "", zip: string = "") {
        const url = PolicyService.baseUrl + "Lookup/CityStateZip/counties"
        let params: HttpParams = new HttpParams()
        params = params.append("state", state);
        params = params.append("city", city);
        params = params.append("zipCode", zip);

        var request = this.http.get<any>(url, { params }).pipe(
            map(data => data.Content?.data?.attributes?.counties)
        );
        return request;
    }

    /**
     * Generate an account number (returns id and mask info). Expects body like { numberingTaskType, requiredInput }
     */
    public generateAccountNumber(body: AccountNumberFields): Observable<any> {
        const url = PolicyService.baseUrl + "Lookup/AccountNumber/actions/generate";

        let payload: any =
        {
            data: {
                attributes: {
                    NumberingTaskType: body?.numberingTaskType,
                    RequiredInput: body?.requiredInput ?? {},
                },
            }
        };

        return this.http.post<any>(url, payload).pipe(
            map((resp) => {
                // Expect response.Content.data.attributes or response.Content.data
                const attrs = resp?.Content?.data?.attributes ?? {};
                return {
                    id: resp?.Content?.data?.id,
                    editMask: attrs.editMask ?? null,
                    validationMask: attrs.validationMask ?? null,
                    helpText: attrs.helpText ?? null,
                };
            }),
            catchError((err: HttpErrorResponse) => {
                throw this.mapError(err, false);
            }),
        );
    }

    public getProducts(): Observable<Product[]> {
        const url = PolicyService.baseUrl + "Products";
        var request = this.http.get<any>(url).pipe(
            map(data => this.mapProductData(data.Content?.data))
        );
        return request;
    }

    private mapProductData(data: any): Product[] | undefined {
        if (!data) return undefined;

        if (Array.isArray(data)) {
            return data.map((item: any) => ({
                id: item.id,
                name: item.attributes.name,
            }));
        } else {
            return [{
                id: data.id,
                name: data.attributes.name,
            }];
        }
    }

    public exportPolicy(storageId: string) {
        const url = PolicyService.baseUrl + "Policies/" + storageId + "/actions/export?IncludeForms=true"
        var request = this.http.post<any>(url, null).pipe(share());
        return request;
    }

    public getHistoricalFormsList(storageId: string, transactionId: number): Observable<Array<HistoricalForm>> {
        const url = PolicyService.baseUrl + "Forms?storageId=" + storageId + "&transactionId=" + transactionId;
        var request = this.http.get<any>(url).pipe(
            map(data => data.Content.data.map((x) => this.mapHistoricalForm(x)))
        );
        return request;
    }

    private mapHistoricalForm({ id, attributes }): HistoricalForm {
        const form: HistoricalForm = {
            id: id ?? '',
            formType: attributes?.formType ?? '',
            number: attributes?.number ?? '',
            description: attributes?.description ?? '',
            edition: attributes?.edition ?? '',
            parsedEdition: attributes?.edition
                ? new Date(attributes.edition)
                : undefined,
            version: attributes?.version ?? 0,
            instanceString: attributes?.instanceString ?? '',
            state: attributes?.formType ?? '',
            sequence: attributes?.sequence ?? 0,
            addedThisTransaction: attributes?.addedThisTransaction ?? false,
        };
        return form;
    }

    public generateHistoricalForms(storageId: string, transactionId: number, groupId: string, formIds: Array<string>)
        : Observable<HistoricalFormsJobId> {

        var body = {
            data: {
                attributes: {
                    formIds,
                }
            }
        };
        const url = PolicyService.baseUrl + "Transactions/" + storageId + "-" + transactionId + "/actions/generateforms/" + groupId;
        var request = this.http.post<any>(url, body).pipe(
            map(data => this.mapHistoricalFormJobId(data.Content.data)),
        );
        return request;

    }

    private mapHistoricalFormJobId({ id, type }): HistoricalFormsJobId {
        const jobId: HistoricalFormsJobId = {
            type: type!,
            jobId: id!,
        };
        return jobId;
    }

    private toDate(d: string | null | undefined): Date | undefined {
        return d ? new Date(d) : undefined;
    }

    public GetBaseUrl(): string {
        return PolicyService.baseUrl;
    }

    handleError = (err: HttpErrorResponse): never => {
        throw this.mapError(err);
    };

    mapError(
        src: HttpErrorResponse,
        notify: boolean = true,
    ): JsonApiErrorResponse {
        const { status, error, message, statusText } = src;

        // not an API Error in the response body - fabricate a JsonApiError
        if (!Array.isArray(error?.errors)) {
            //console.error(JSON.stringify(src, null, 2));
            const { statusText, headers } = src;

            let apiError: JsonApiError;
            if (401 == status && 'Bearer' === headers.get('WWW-Authenticate'))
                apiError = {
                    status,
                    code: 'CLIENT_InvalidJwt',
                    title: statusText,
                    detail: 'JWT Validation Failed',
                };
            if (404 == status)
                apiError = {
                    status,
                    code: 'CLIENT_NoSuchRoute',
                    title: statusText,
                    detail: message,
                };
            else
                apiError = {
                    status,
                    code: 'CLIENT_Other',
                    title: statusText,
                };
            return {
                name: JSON_API_ERROR_RESPONSE,
                notify: notify,
                status,
                errors: [apiError],
            };
        }

        const { errors, meta } = error as {
            errors: ApiError[];
            meta?: { StackTrace?: string };
        };
        return this.clean({
            name: JSON_API_ERROR_RESPONSE,
            notify: notify,
            status,
            errors: errors.map(
                ({ status, ...rest }) =>
                    this.clean({
                        ...rest,
                        status: undefined === status ? undefined : Number(status),
                    }) as JsonApiError,
            ),
            stackTrace: meta?.StackTrace,
        });
    }

    /** strip null and undefined */
    clean<T extends object>(src: T): T {
        return Object.entries(src).reduce((p, [k, v]) => {
            if (null !== v && undefined !== v) p[k] = v;
            return p;
        }, {} as any) as T;
    }
}

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
export class CustomHttpParameterCodec implements HttpParameterCodec {
    encodeKey(k: string): string {
        return encodeURIComponent(k);
    }
    encodeValue(v: string): string {
        return encodeURIComponent(v);
    }
    decodeKey(k: string): string {
        return decodeURIComponent(k);
    }
    decodeValue(v: string): string {
        return decodeURIComponent(v);
    }
}
